import { FC, useContext } from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { TrainingFolderRequest } from "@/features/training/model/Training/TrainingFolder";
import { CustomCloseButton, CustomSaveButton } from "@/features/training/ui";
import CustomInput from "@/shared/forms/CustomInput";
import { AlertToast } from "@/shared/notifications/AlertToast";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { useMutateCreateTrainingFolder } from "@/service/TrainingMutations";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


interface NewTrainingFolderProps {
	handleClose: () => void;
	open: boolean;
}

const validationSchema = Yup.object({
	Name: Yup.string().trim().required("Name is required"),
});

export const NewTrainingFolder: FC<NewTrainingFolderProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const createTrainingFolder = useMutateCreateTrainingFolder();

	const handleSave = async (request: TrainingFolderRequest) => {
		try {
			await createTrainingFolder(request);
			props.handleClose();
			await AlertToast.success();
		} catch (error) {
			console.error(error);
			await AlertToast.failed();
		}
	};

	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	return (
		<Dialog
			onClose={props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="sm"
			fullScreen={isMobile}
			aria-labelledby="new-folder-title"
			PaperProps={{
				sx: sharedTaskDialogPaperSx(theme, isMobile),
			}}
		>
			<DialogTitle
				id="new-folder-title"
				component="div"
				sx={{
					...sharedTaskDialogHeaderSx(theme),
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 800,
						letterSpacing: "0.08em",
						color: "primary.main",
						mb: 0.4,
					}}
				>
					<Translate String="Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingFolder.L96C6.TRAINING">TRAINING</Translate>
				</Typography>
				<Typography
					variant="h6"
					component="h2"
					sx={{
						fontWeight: 700,
						color: "text.primary",
						pr: 5,
						mb: 0.75,
					}}
				>
					<Translate String="Training.NewTrainingFolder.Title">
						New Folder
					</Translate>
				</Typography>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
						lineHeight: 1.5,
						pr: 5,
					}}
				>
					<Translate String="Training.NewTrainingFolder.Subtitle">
						Create a folder to organize related courses and links.
					</Translate>
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingFolder.L126C17.Close", "Close")}
					onClick={props.handleClose}
					size="small"
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						...closeButtonSx,
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent
				sx={{
					...sharedTaskDialogContentSx(theme),
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
				}}
			>
				<Formik
					initialValues={{ Name: "" }}
					validationSchema={validationSchema}
					validateOnMount
					onSubmit={handleSave}
				>
					{({ isValid, dirty, isSubmitting }) => (
						<Form>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<CustomInput
										id="Name"
										type="text"
										label="Name"
										name="Name"
										translationString="Training.NewTrainingFolder.Name"
										placeholder="e.g., Safety Procedures"
										onKeyDown={(e) => e.stopPropagation()}
										autoFocus
										fullWidth
										required
									/>
									<Typography
										variant="caption"
										sx={{
											display: "block",
											mt: 0.5,
											color: "text.secondary",
										}}
									>
										<Translate String="Training.NewTrainingFolder.Help">
											Keep it short and descriptive (you can rename later).
										</Translate>
									</Typography>
								</Grid>
							</Grid>

							<DialogActions
								sx={{
									...sharedTaskDialogActionsSx(theme, isMobile),
									px: 0,
									pt: 2,
									mt: 3,
									display: "flex",
									justifyContent: "flex-end",
									gap: 1.5,
									flexWrap: "wrap",
								}}
							>
								<CustomCloseButton
									handleClose={props.handleClose}
									disabled={isSubmitting}
								/>
								<CustomSaveButton
									loading={isSubmitting}
									disabled={!isValid || !dirty || isSubmitting}
								/>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};
